/**
 * @file S3BrowserContext.tsx - S3 File Browser Modal State Management
 *
 * This module provides a React context for managing the S3 file browser modal state.
 * It allows any component in the app to open/close the S3 browser modal without
 * prop drilling or complex state management.
 *
 * The S3 browser allows authenticated users to:
 * - Browse their S3 workspace folders and files
 * - Upload new Excalidraw scenes
 * - Download/load existing scenes
 * - Delete files and folders
 * - Create new folders
 *
 * Note: Requires valid JWT authentication - the S3 API endpoints validate
 * the token on each request.
 */
import React, { createContext, useContext, useState } from "react";

import { S3BrowserModal } from "./S3BrowserModal";

import type { ReactNode } from "react";

/**
 * Context type for S3 browser modal state.
 *
 * @property openS3Modal - Opens the S3 browser modal
 * @property closeS3Modal - Closes the S3 browser modal
 * @property isS3ModalOpen - Current open state of the modal
 */
interface S3BrowserContextType {
  openS3Modal: () => void;
  closeS3Modal: () => void;
  isS3ModalOpen: boolean;
}

const S3BrowserContext = createContext<S3BrowserContextType | undefined>(
  undefined,
);

/**
 * Hook to access S3 browser modal controls.
 * Must be used within S3BrowserProvider.
 *
 * @returns S3BrowserContextType with modal state and controls
 * @throws Error if used outside S3BrowserProvider
 *
 * @example
 * const { openS3Modal } = useS3Browser();
 * // Later, to open the browser:
 * openS3Modal();
 */
export const useS3Browser = () => {
  const context = useContext(S3BrowserContext);
  if (context === undefined) {
    throw new Error("useS3Browser must be used within a S3BrowserProvider");
  }
  return context;
};

interface S3BrowserProviderProps {
  children: ReactNode;
}

/**
 * Provider component for S3 browser modal state.
 * Wraps application components that need access to the S3 browser.
 * Renders the S3BrowserModal when isS3ModalOpen is true.
 *
 * @param children - Child components that will have access to useS3Browser hook
 */
export const S3BrowserProvider = ({ children }: S3BrowserProviderProps) => {
  const [isS3ModalOpen, setIsS3ModalOpen] = useState(false);

  const openS3Modal = () => setIsS3ModalOpen(true);
  const closeS3Modal = () => setIsS3ModalOpen(false);

  const value = {
    openS3Modal,
    closeS3Modal,
    isS3ModalOpen,
  };

  return (
    <S3BrowserContext.Provider value={value}>
      {children}
      {isS3ModalOpen && <S3BrowserModal onClose={closeS3Modal} />}
    </S3BrowserContext.Provider>
  );
};
